
import { motion } from 'framer-motion';
import { useState } from 'react';

const PolicyRules = () => {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);

  const rulesData = [
    {
      icon: "👤",
      title: "申报主体",
      conclusion: "以市场内登记主体名义申报",
      description: "可使用个体工商户身份进行申报",
      limitation: "必须在指定市场内完成工商登记",
      // 修正了字符串中的引号冲突
      misconception: "不是“随便挂靠主体”，需要真实的商事登记关系",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: "📦",
      title: "商品范围",
      conclusion: "日用消费品为主，多品类混装",
      description: "支持多种商品在同一票报关单中申报",
      limitation: "禁限类商品不可适用1039方式",
      misconception: "不是所有商品都可以，需要符合海关正面清单要求",
      color: "from-green-500 to-green-600"
    },
    {
      icon: "💵",
      title: "金额限制",
      conclusion: "单票不超过15万美元",
      description: "按报关单计算，不是按单个商品计算",
      limitation: "超过15万美元必须采用一般贸易方式",
      misconception: "不是总金额限制，而是每票报关单的限制",
      color: "from-purple-500 to-purple-600"
    },
    {
      icon: "🏛️",
      title: "税务处理",
      conclusion: "免征增值税，不退税",
      description: "无需取得进项发票，解决无票出口难题",
      limitation: "不享受出口退税政策",
      misconception: "不是变相退税，而是免征增值税的政策安排",
      color: "from-orange-500 to-orange-600"
    },
    {
      icon: "🚛",
      title: "物流要求",
      conclusion: "必须通过指定物流渠道",
      description: "与海关监管系统对接的物流服务商",
      limitation: "不能自行选择任意物流公司",
      misconception: "不是增加成本，而是确保合规监管的必要环节",
      color: "from-teal-500 to-teal-600"
    },
    {
      icon: "💰",
      title: "收汇结汇",
      conclusion: "可通过代收外汇服务结汇",
      description: "解决个体工商户收汇难题",
      limitation: "必须通过合规的代收外汇主体",
      misconception: "不是逃避外汇监管，而是规范化的收汇渠道",
      color: "from-red-500 to-red-600"
    }
  ];

  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-[1440px] mx-auto px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl font-bold text-primary mb-4">
            1039核心规则拆解
          </h2>
          <p className="text-gray-600 text-lg">
            清晰了解规则边界，避免常见误区
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rulesData.map((rule, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="relative group"
            >
              <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                {/* 渐变头部 */}
                <div className={`h-2 bg-gradient-to-r ${rule.color}`}></div>

                <div className="p-6">
                  {/* 规则图标和标题 */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="text-2xl">{rule.icon}</div>
                    <h3 className="text-xl font-bold text-primary">{rule.title}</h3>
                  </div>

                  {/* 一句话结论 */}
                  <div className="mb-4">
                    <div className="bg-accent/10 border-l-4 border-accent p-3 rounded-r-lg">
                      <p className="font-semibold text-primary">{rule.conclusion}</p>
                    </div>
                  </div>

                  {/* 适用说明 */}
                  <div className="mb-3">
                    <div className="flex items-start gap-2">
                      <i className="ri-check-line text-green-500 mt-1"></i>
                      <p className="text-gray-700 text-sm">{rule.description}</p>
                    </div>
                  </div>

                  {/* 限制说明 */}
                  <div className="mb-4">
                    <div className="flex items-start gap-2">
                      <i className="ri-alert-line text-amber-500 mt-1"></i>
                      <p className="text-gray-600 text-sm">{rule.limitation}</p>
                    </div>
                  </div>

                  {/* 常见误解提示 */}
                  <div className="border-t border-gray-100 pt-4">
                    <button
                      onClick={() =>
                        setExpandedCard(expandedCard === index ? null : index)
                      }
                      className="w-full text-left flex items-center justify-between text-sm font-medium text-primary hover:text-primary-dark transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <span>常见误区提示</span>
                      <i
                        className={`ri-arrow-down-s-line transition-transform duration-200 ${
                          expandedCard === index ? "rotate-180" : ""
                        }`}
                      ></i>
                    </button>

                    {expandedCard === index && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-3 p-3 bg-red-50 border border-red-100 rounded-lg"
                      >
                        <div className="flex items-start gap-2">
                          <i className="ri-error-warning-line text-red-500 mt-1"></i>
                          <p className="text-red-700 text-sm">{rule.misconception}</p>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* 重要提示 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12 bg-gradient-to-r from-primary/5 to-accent/5 rounded-xl p-8 border border-primary/10"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-line text-accent text-xl"></i>
            </div>
            <div>
              <h3 className="font-bold text-primary mb-2">规则理解要点</h3>
              <p className="text-gray-700 leading-relaxed">
                以上规则不是限制，而是为了确保合规性和可操作性。每个规则都有其政策逻辑和监管考量，理解规则边界有助于更好地利用1039政策优势。
                如果您的业务模式符合以上规则要求，建议进入适配性测试环节。
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PolicyRules;
